import type { TFunction } from "i18next";

import { formatCount } from "@/lib/format";

import type { AdherenceIndexState } from "../data/adherenceIndex";
import { formatBrl } from "../diagnosticData";
import { NO_VALUE } from "../sectionState";
import type { DiagnosticSummary } from "./summaryData";

/**
 * O que dizer quando o índice de aderência não tem número, com as mesmas frases
 * do tile do Ato 1: vazio diz que nada foi avaliado, e qualquer outro estado (não
 * coletado ou com falha) diz que não deu para ler. `counted` sem base (tudo sem
 * disposição) cai em "vazio": 0% afirmaria que nada é padrão.
 */
export function adherenceUnavailableHint(state: AdherenceIndexState, t: TFunction): string {
  if (state.kind === "empty" || state.kind === "counted") {
    return t("No scope item, WRICEF or GAP assessed yet.");
  }
  return t("The adherence index could not be read right now.");
}

export interface Figure {
  label: string;
  value: string;
  hint?: string;
}

/** O prazo em meses com uma casa, como o Ato 4; `undefined` sem duração utilizável. */
export function monthsText(timeline: DiagnosticSummary["investment"]["timeline"]): string | undefined {
  const months = timeline?.months ?? undefined;
  return months === undefined ? undefined : formatCount(months, { decimals: 1 });
}

/**
 * Os três números do investimento (software, projeto e prazo), com o que dizer
 * quando a fonte ainda não existe. A faixa de destaque e o Bloco 4 desenham os
 * mesmos, e por isso a regra mora num lugar só.
 */
export function investmentFigures(
  investment: DiagnosticSummary["investment"],
  t: TFunction,
): { software: Figure; project: Figure; timeline: Figure } {
  const { software, project, timeline } = investment;
  const months = monthsText(timeline);

  const softwareFigure = ((): Figure => {
    switch (software.state) {
      case "pricing_app":
        return {
          label: t("SAP software · TCV"),
          value: formatBrl(software.tcv),
          hint:
            software.periodMonths === undefined
              ? t("{{acv}} a year", { acv: formatBrl(software.acv) })
              : t("{{acv}} a year · {{months}}-month contract", {
                  acv: formatBrl(software.acv),
                  months: formatCount(software.periodMonths),
                }),
        };
      case "calculated":
        return {
          label: t("SAP software · ACV"),
          value: formatBrl(software.acv),
          hint: t("Annual value"),
        };
      case "awaiting_pricing_app":
        return {
          label: t("SAP software · TCV"),
          value: NO_VALUE,
          hint: t("Awaiting the Pricing App BOM"),
        };
      case "unavailable":
        return {
          label: t("SAP software · TCV"),
          value: NO_VALUE,
          hint: t("The software value could not be read right now."),
        };
    }
  })();

  const projectFigure: Figure = {
    label: t("Migration project"),
    value: project.state === "ready" ? formatBrl(project.offered) : NO_VALUE,
    hint:
      project.state === "ready"
        ? t("One-off")
        : project.state === "awaiting"
          ? t("Awaiting commercial calibration")
          : t("The commercial calibration could not be read."),
  };

  const timelineFigure: Figure = {
    label: t("Timeline"),
    value: months === undefined ? NO_VALUE : t("{{months}} months", { months }),
    hint:
      months !== undefined
        ? t("Project duration")
        : timeline === null
          ? t("Not calibrated yet — the timeline comes from the calibration step.")
          : t("This published version does not carry the project duration — calibrate again to see it."),
  };

  return { software: softwareFigure, project: projectFigure, timeline: timelineFigure };
}
